import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatApiErrorDetail } from '@/lib/authApi';
import { FORGOT_PASSWORD } from '@/constants/testIds';
import { Loader2 } from 'lucide-react';

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await forgotPassword(email);
      const resetLink = data.reset_token
        ? `${window.location.origin}/reset-password?token=${data.reset_token}`
        : null;
      setDone({ message: data.message, resetLink });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email and we'll create a reset link"
      footer={<Link to="/login" className="text-primary font-medium hover:underline" data-testid={FORGOT_PASSWORD.backToLoginLink}>Back to sign in</Link>}
    >
      {done ? (
        <div className="space-y-4" data-testid="forgot-password-success">
          <p className="text-sm text-muted-foreground">{done.message}</p>
          {done.resetLink && (
            <div className="rounded-lg border border-border bg-muted/50 p-4 text-sm">
              <p className="mb-1 font-medium">Dev mode — email delivery not configured</p>
              <p className="mb-3 text-muted-foreground">Use this link to set a new password:</p>
              <a href={done.resetLink} className="text-primary break-all hover:underline" data-testid="forgot-password-reset-link">{done.resetLink}</a>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid={FORGOT_PASSWORD.emailInput} />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="forgot-password-error">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading} data-testid={FORGOT_PASSWORD.submitButton}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
